import { Link } from 'react-router-dom';
import { getPublished } from '@/data/loader';
import { useI18n } from '@/hooks/useI18n';
import { EntityCard } from '@/components/EntityCard';
import { PontosTuristicosSEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { OptimizedImage } from '@/components/OptimizedImage';
import { AuthorBox } from '@/components/AuthorBox';
import { PontosTuristicosQuiz } from '@/components/PontosTuristicosQuiz';

import { defaultAuthor } from '@/data/author';
import bio from '@/data/biodiversidade.json';

/* ═══════════════════════════════════════════════════════════════
   PILAR — Pontos Turísticos da Ilha do Mel
   Jornada: descobrir → comparar → explorar por tipo → entender a
   natureza (Biodiversidade) → tirar dúvidas.
   ═══════════════════════════════════════════════════════════════ */

// Agrupa os "type" das atrações em famílias legíveis
const GRUPOS: { key: string; titulo: string; desc: string; lead: string; types: string[] }[] = [
  {
    key: 'historicos',
    titulo: 'Históricos e culturais',
    desc: 'Monumentos e marcos que contam a história da ilha.',
    lead: 'Do alto do Farol das Conchas — erguido em 1872 por ordem de Dom Pedro II, no topo de cerca de 150 degraus — à Fortaleza de Nossa Senhora dos Prazeres, construída entre 1767 e 1769 com canhões do século XVIII, os marcos históricos da ilha contam a saga do litoral paranaense. Some-se a eles a pequena Capela de São Francisco de Assis, no alto do Morro Nhá Pina, em Encantadas.',
    types: ['monumento-historico', 'religioso'],
  },
  {
    key: 'natureza',
    titulo: 'Natureza e formações',
    desc: 'Grutas, mirantes, piscinas naturais e paisagens que só se alcança a pé ou de barco.',
    lead: 'É aqui que a ilha mostra por que é cartão-postal: a Gruta das Encantadas e sua lenda das sereias (visitável só na maré baixa), a piscina natural da Bacia da Sereia, o Istmo de apenas 30 metros de largura e a Ponta Oeste, o extremo mais selvagem. Fecha a lista a Baía dos Golfinhos, onde os botos-cinza cruzam o mar entre a Ilha do Mel e a Ilha das Peças.',
    types: ['formacao-natural', 'formacao-geografica', 'mirante', 'trilha'],
  },
  {
    key: 'conservacao',
    titulo: 'Unidades de conservação',
    desc: 'As áreas protegidas que mantêm a ilha preservada.',
    lead: 'Cerca de 93% da ilha é área de proteção integral. O Parque Estadual da Ilha do Mel (criado em 2002, aberto à visitação) guarda as praias, os costões e as trilhas do sul; a Estação Ecológica (1982, ~2.240 ha, fechada ao público) preserva a mata atlântica e os manguezais do norte. Juntas, são a razão de a ilha continuar quase intocada.',
    types: ['unidade-conservacao'],
  },
];

const FAQ = [
  {
    q: 'Quais são os principais pontos turísticos da Ilha do Mel?',
    a: 'Os mais visitados são o Farol das Conchas, a Fortaleza de Nossa Senhora dos Prazeres, a Gruta das Encantadas, o Istmo (Passa-Passa) e a Baía dos Golfinhos. Todos são gratuitos, e a maioria é acessível por trilhas curtas a partir das vilas de Nova Brasília e Encantadas.',
  },
  {
    q: 'Precisa pagar para visitar os pontos turísticos da ilha?',
    a: 'A grande maioria é gratuita — Farol, Fortaleza, Gruta, Morro do Sabão, Istmo e Ponta Oeste não cobram entrada. Só há custo quando o acesso depende de passeio de barco, como a Baía dos Golfinhos.',
  },
  {
    q: 'Qual a melhor época para conhecer os pontos turísticos?',
    a: 'O ano todo funciona, mas o verão (dezembro a março) tem mar mais quente e dias ensolarados; primavera e outono trazem trilhas mais tranquilas e menos calor. Atrações como a Gruta das Encantadas dependem da maré baixa (geralmente pela manhã).',
  },
  {
    q: 'Quantos degraus tem o Farol das Conchas?',
    a: 'São cerca de 150 degraus até o topo, precedidos de uma caminhada plana de uns 30 minutos desde Nova Brasília. Não se entra na torre do farol, mas o mirante externo tem vista de 360° da ilha inteira.',
  },
  {
    q: 'O Farol das Conchas é grátis? Dá para subir?',
    a: 'Sim, a visita é gratuita. Você sobe os ~150 degraus até a base do farol (a torre em si não é aberta ao público), de onde se tem a melhor vista da Ilha do Mel.',
  },
  {
    q: 'Qual a melhor hora para visitar o Farol?',
    a: 'Início da manhã (menos gente) ou fim de tarde, pelo pôr do sol. Evite o meio-dia: não há sombra na subida e a luz forte atrapalha as fotos.',
  },
  {
    q: 'Como saber a maré para visitar a Gruta das Encantadas?',
    a: 'A Gruta só pode ser visitada e adentrada na maré baixa, geralmente pela manhã (por volta das 8h às 13h). Consulte a tábua de maré da Ilha do Mel antes de ir — na maré alta, a água cobre a entrada.',
  },
  ...bio.faq,
];

export function PontosTuristicosPage() {
  const { lang } = useI18n();
  const atracoes = getPublished('atracao', lang);
  const grupos = GRUPOS.map(g => ({
    ...g,
    items: atracoes.filter((a: any) => g.types.includes(a.type)),
  })).filter(g => g.items.length > 0);

  return (
    <>
      <PontosTuristicosSEO atracoes={atracoes} />
      <div>
        {/* Hero */}
        <div className="relative mb-10">
          <div className="relative h-48 sm:h-56">
            <OptimizedImage
              src="/images/morro-farol-vista.jpg"
              alt="Pontos turísticos da Ilha do Mel"
              className="w-full h-full object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-sand-100/95 via-sand-100/40 to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 pb-6">
            <Container>
              <h1 className="font-display text-4xl sm:text-5xl text-warm-dark">
                Pontos Turísticos da Ilha do Mel
              </h1>
            </Container>
          </div>
        </div>

        <Container className="pb-20">
          {/* Intro */}
          <p className="font-editorial text-lg text-warm-gray leading-relaxed max-w-3xl mb-14">
            A <Link to="/" className="text-teal-deep font-medium hover:underline">Ilha do Mel</Link>{' '}
            concentra, num pedaço de Mata Atlântica sem carros, alguns dos marcos mais bonitos do
            litoral do Paraná: um farol imperial de 1872, uma fortaleza do século XVIII, grutas de
            lenda, mirantes de tirar o fôlego e uma baía onde os botos-cinza cruzam o mar. Reunimos
            aqui todos os pontos turísticos da ilha — o que são, como chegar e a melhor hora de cada
            um.
          </p>

          {/* Comparativo */}
          <section className="mb-16" id="comparativo">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">
              Compare os pontos turísticos
            </h2>
            <p className="font-body text-warm-gray mb-6">
              Vila, tipo de acesso e melhor época — de relance.
            </p>
            <div className="overflow-x-auto rounded-xl border border-sand-200 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-teal-deep text-white">
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">Ponto</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">Vila</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">Acesso</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">Tempo</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">Entrada</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">Melhor época</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand-100">
                  {atracoes.map((a: any, i: number) => (
                    <tr key={a.handle} className={i % 2 === 0 ? 'bg-white' : 'bg-sand-50'}>
                      <td className="px-4 py-3 font-body font-medium text-teal-deep whitespace-nowrap">
                        <Link to={`/ponto-turistico/${a.handle}`} className="hover:underline">
                          {a.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3 font-body text-warm-gray">{a.vilarejoName}</td>
                      <td className="px-4 py-3 font-body text-warm-gray">{a.difficulty}</td>
                      <td className="px-4 py-3 font-body text-warm-gray whitespace-nowrap">{a.duration}</td>
                      <td className="px-4 py-3 font-body text-warm-gray">{a.price}</td>
                      <td className="px-4 py-3 font-body text-warm-gray text-xs">{a.bestTime}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Quiz — recomenda um ponto pelo perfil (eixos vindos do radar de conteúdo) */}
          <PontosTuristicosQuiz atracoes={atracoes} />

          {/* Cards por tipo */}
          {grupos.map(g => (
            <section className="mb-16" id={g.key} key={g.key}>
              <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">{g.titulo}</h2>
              <p className="font-body text-warm-gray mb-3">{g.desc}</p>
              <p className="font-body text-warm-gray leading-relaxed max-w-3xl mb-8">{g.lead}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {g.items.map((a: any) => (
                  <EntityCard key={a.handle} entity={a} category="ponto-turistico" />
                ))}
              </div>
            </section>
          ))}

          {/* Biodiversidade */}
          <section className="mb-16 bg-sand-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 rounded-2xl" id="biodiversidade">
            <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
              {bio.overline}
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mt-2 mb-3">
              {bio.sectionTitle}
            </h2>
            <div className="font-editorial text-lg text-warm-gray leading-relaxed max-w-3xl mb-8 [&_a]:text-teal-deep [&_a]:underline">
              <p dangerouslySetInnerHTML={{ __html: bio.intro.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>') }} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {bio.stats.map((s: any) => (
                <div key={s.label} className="bg-white rounded-xl p-5 border border-sand-200 text-center">
                  <div className="font-display text-3xl text-teal-deep">{s.value}</div>
                  <div className="font-body text-xs text-warm-gray mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="font-display text-xl text-warm-dark mb-3">Ecossistemas</h3>
                <ul className="space-y-3">
                  {bio.ecossistemas.map((e: any) => (
                    <li key={e.nome} className="font-body text-sm text-warm-gray">
                      <span className="font-semibold text-warm-dark">{e.nome}.</span> {e.desc}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-display text-xl text-warm-dark mb-3">Fauna</h3>
                <ul className="space-y-3">
                  {bio.fauna.map((f: any) => (
                    <li key={f.grupo} className="font-body text-sm text-warm-gray">
                      <span className="font-semibold text-warm-dark">{f.grupo}:</span> {f.exemplos}. {f.desc}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-teal-light/5 border border-teal-light/20 rounded-xl p-5">
              <p className="font-body text-sm text-warm-gray leading-relaxed">{bio.conservacao}</p>
            </div>

            <div className="mt-6">
              <h3 className="font-display text-lg text-warm-dark mb-3">Turismo consciente</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {bio.dicasConduta.map((d: string, i: number) => (
                  <div key={i} className="flex gap-3 p-4 bg-white rounded-xl border border-sand-200">
                    <span className="text-teal-deep font-bold flex-shrink-0 mt-0.5">✓</span>
                    <p className="font-body text-sm text-warm-gray leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Como chegar — colocado no final para não tirar o usuário da página no início */}
          <div className="mb-14 bg-teal-light/5 border border-teal-light/20 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 max-w-3xl" id="como-chegar">
            <span className="text-3xl flex-shrink-0" aria-hidden="true">⛴️</span>
            <div className="flex-1">
              <h2 className="font-display text-lg text-warm-dark mb-1">Como chegar na Ilha do Mel</h2>
              <p className="font-body text-sm text-warm-gray leading-relaxed">
                A ilha não tem carros e só se chega de barco — de Pontal do Sul (~30 min) ou de Paranaguá
                (~1h30). Você desembarca em Nova Brasília ou Encantadas, e é dali que saem as trilhas para
                os pontos acima.
              </p>
            </div>
            <Link
              to="/como-chegar"
              className="font-body text-sm font-semibold text-teal-deep hover:underline whitespace-nowrap"
            >
              Guia de como chegar →
            </Link>
          </div>

          {/* FAQ */}
          <section className="max-w-3xl" id="faq">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-8">Perguntas frequentes</h2>
            <div className="space-y-3">
              {FAQ.map((item, i) => (
                <details key={i} className="group bg-white border border-sand-200 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
                    <span className="font-display text-base text-warm-dark pr-4">{item.q}</span>
                    <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">▼</span>
                  </summary>
                  <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">{item.a}</div>
                </details>
              ))}
            </div>
          </section>

          <AuthorBox author={defaultAuthor} />
        </Container>
      </div>
    </>
  );
}
